'use client';

import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/common/command';
import type { LocationPrediction } from '@/lib/server/geolocation/types';
import { cn } from '@/lib/utils/utils';
import GeolocationButton from './GeolocationButton';
import { useLocationAutocomplete } from './useLocationAutocomplete';
export type LocationFormProps = {
  onLocationChange?: (location: import('@/lib/server/geolocation/types').Geolocation) => void;
  placeholder?: string;
  value?: string;
  minQueryLength?: number;
  updateUrlParams?: boolean;
  urlParamNames?: {
    lat?: string;
    lng?: string;
    address?: string;
  };
  urlPath?: string;
  scrollToResults?: boolean;
  className?: string;
  inputClassName?: string;
  showGeolocationButton?: boolean;
  geolocationButtonProps?: {
    variant?: 'default' | 'outline' | 'ghost';
    size?: 'sm' | 'default' | 'lg';
    iconOnly?: boolean;
    className?: string;
  };
  renderItem?: (prediction: LocationPrediction) => React.ReactNode;
  showErrorMessage?: boolean;
  wrapperClassName?: string;
};
export default function LocationForm(t0) {
  const $ = _c(86);
  const {
    placeholder: t1,
    value: controlledValue,
    minQueryLength: t2,
    updateUrlParams: t3,
    urlParamNames: t4,
    urlPath: t5,
    scrollToResults: t6,
    onLocationChange,
    className,
    inputClassName,
    showGeolocationButton: t7,
    geolocationButtonProps: t8,
    renderItem,
    showErrorMessage: t9,
    wrapperClassName
  } = t0;
  const placeholder = t1 === undefined ? "City, Town, Postcode" : t1;
  const minQueryLength = t2 === undefined ? 2 : t2;
  const updateUrlParams = t3 === undefined ? false : t3;
  let t10;
  if ($[0] !== t4) {
    t10 = t4 === undefined ? {
      lat: "lat",
      lng: "lng",
      address: "address"
    } : t4;
    $[0] = t4;
    $[1] = t10;
  } else {
    t10 = $[1];
  }
  const urlParamNames = t10;
  const urlPath = t5 === undefined ? "/locations" : t5;
  const scrollToResults = t6 === undefined ? false : t6;
  const showGeolocationButton = t7 === undefined ? false : t7;
  let t11;
  if ($[2] !== t8) {
    t11 = t8 === undefined ? {} : t8;
    $[2] = t8;
    $[3] = t11;
  } else {
    t11 = $[3];
  }
  const geolocationButtonProps = t11;
  const showErrorMessage = t9 === undefined ? true : t9;
  let t12;
  if ($[4] !== controlledValue || $[5] !== minQueryLength || $[6] !== onLocationChange || $[7] !== scrollToResults || $[8] !== updateUrlParams || $[9] !== urlParamNames || $[10] !== urlPath) {
    t12 = {
      controlledValue,
      minQueryLength,
      updateUrlParams,
      urlParamNames,
      urlPath,
      scrollToResults,
      onLocationChange
    };
    $[4] = controlledValue;
    $[5] = minQueryLength;
    $[6] = onLocationChange;
    $[7] = scrollToResults;
    $[8] = updateUrlParams;
    $[9] = urlParamNames;
    $[10] = urlPath;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  const {
    isPending,
    autocompleteResults,
    locationQuery,
    setLocationQuery,
    isOpen,
    setIsOpen,
    errorMessage,
    handleKeyDown,
    handleLocationFound,
    handleLocationError,
    onSelectAutocomplete,
    defaultRenderItem
  } = useLocationAutocomplete(t12);
  let t13;
  if ($[12] !== geolocationButtonProps || $[13] !== showGeolocationButton) {
    t13 = showGeolocationButton && geolocationButtonProps.className?.includes("absolute");
    $[12] = geolocationButtonProps;
    $[13] = showGeolocationButton;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  const isAbsoluteButton = t13;
  let t14;
  if ($[15] !== wrapperClassName) {
    t14 = cn("space-y-4", wrapperClassName);
    $[15] = wrapperClassName;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  const t15 = !isAbsoluteButton && "rounded-full";
  const t16 = !isAbsoluteButton && "**:data-[slot=command-input-wrapper]:rounded-full";
  const t17 = showGeolocationButton && !isAbsoluteButton && "flex flex-row justify-between";
  let t18;
  if ($[17] !== className || $[18] !== t15 || $[19] !== t16 || $[20] !== t17) {
    t18 = cn("bg-white overflow-visible", t15, t16, "**:data-[slot=command-input-wrapper]:p-0", "**:data-[slot=command-input-wrapper]:bg-transparent", "**:data-[slot=command-input-wrapper]:focus-within:ring-0", t17, className);
    $[17] = className;
    $[18] = t15;
    $[19] = t16;
    $[20] = t17;
    $[21] = t18;
  } else {
    t18 = $[21];
  }
  const t19 = isAbsoluteButton ? "w-full" : "flex-1";
  let t20;
  if ($[22] !== t19) {
    t20 = cn("relative flex items-center", t19);
    $[22] = t19;
    $[23] = t20;
  } else {
    t20 = $[23];
  }
  let t21;
  if ($[24] !== setIsOpen || $[25] !== setLocationQuery) {
    t21 = value => {
      setLocationQuery(value);
      if (value) {
        setIsOpen(true);
      }
    };
    $[24] = setIsOpen;
    $[25] = setLocationQuery;
    $[26] = t21;
  } else {
    t21 = $[26];
  }
  let t22;
  if ($[27] !== autocompleteResults.length || $[28] !== locationQuery || $[29] !== setIsOpen) {
    t22 = () => {
      if (locationQuery || autocompleteResults.length > 0) {
        setIsOpen(true);
      }
    };
    $[27] = autocompleteResults.length;
    $[28] = locationQuery;
    $[29] = setIsOpen;
    $[30] = t22;
  } else {
    t22 = $[30];
  }
  const t23 = !isAbsoluteButton && "rounded-full flex-1";
  const t24 = isAbsoluteButton && "w-full rounded-full";
  let t25;
  if ($[31] !== inputClassName || $[32] !== t23 || $[33] !== t24) {
    t25 = cn("h-14 border-none text-lg text-blue-300 focus:ring-0", t23, t24, inputClassName);
    $[31] = inputClassName;
    $[32] = t23;
    $[33] = t24;
    $[34] = t25;
  } else {
    t25 = $[34];
  }
  let t26;
  if ($[35] !== handleKeyDown || $[36] !== locationQuery || $[37] !== placeholder || $[38] !== t21 || $[39] !== t22 || $[40] !== t25) {
    t26 = <CommandInput value={locationQuery} onValueChange={t21} onFocus={t22} onKeyDown={handleKeyDown} placeholder={placeholder} icon={null} className={t25} aria-label="Location search input" />;
    $[35] = handleKeyDown;
    $[36] = locationQuery;
    $[37] = placeholder;
    $[38] = t21;
    $[39] = t22;
    $[40] = t25;
    $[41] = t26;
  } else {
    t26 = $[41];
  }
  let t27;
  if ($[42] !== geolocationButtonProps || $[43] !== handleLocationError || $[44] !== handleLocationFound || $[45] !== isAbsoluteButton || $[46] !== showGeolocationButton) {
    t27 = showGeolocationButton && <GeolocationButton onLocationFound={handleLocationFound} onError={handleLocationError} variant={geolocationButtonProps.variant || "ghost"} size={geolocationButtonProps.size || "sm"} iconOnly={geolocationButtonProps.iconOnly ?? true} className={cn(!isAbsoluteButton && "h-auto p-0 shrink-0", geolocationButtonProps.className)} />;
    $[42] = geolocationButtonProps;
    $[43] = handleLocationError;
    $[44] = handleLocationFound;
    $[45] = isAbsoluteButton;
    $[46] = showGeolocationButton;
    $[47] = t27;
  } else {
    t27 = $[47];
  }
  let t28;
  if ($[48] !== t20 || $[49] !== t26 || $[50] !== t27) {
    t28 = <div className={t20}>{t26}{t27}</div>;
    $[48] = t20;
    $[49] = t26;
    $[50] = t27;
    $[51] = t28;
  } else {
    t28 = $[51];
  }
  const t29 = !(isOpen && (autocompleteResults.length > 0 || isPending)) && "hidden";
  let t30;
  if ($[52] !== t29) {
    t30 = cn("absolute top-full left-0 z-50 w-full mt-1 rounded-lg bg-white shadow-lg max-h-72 overflow-auto", t29);
    $[52] = t29;
    $[53] = t30;
  } else {
    t30 = $[53];
  }
  const t31 = isPending ? "Searching..." : "No locations found.";
  let t32;
  if ($[54] !== t31) {
    t32 = <CommandEmpty>{t31}</CommandEmpty>;
    $[54] = t31;
    $[55] = t32;
  } else {
    t32 = $[55];
  }
  let t33;
  if ($[56] !== autocompleteResults || $[57] !== defaultRenderItem || $[58] !== onSelectAutocomplete || $[59] !== renderItem) {
    let t34;
    if ($[61] !== defaultRenderItem || $[62] !== onSelectAutocomplete || $[63] !== renderItem) {
      t34 = prediction => <CommandItem key={prediction.place_id} value={prediction.description} onSelect={() => onSelectAutocomplete(prediction)} className="px-4 py-2 cursor-pointer">{renderItem ? renderItem(prediction) : defaultRenderItem(prediction)}</CommandItem>;
      $[61] = defaultRenderItem;
      $[62] = onSelectAutocomplete;
      $[63] = renderItem;
      $[64] = t34;
    } else {
      t34 = $[64];
    }
    t33 = autocompleteResults.map(t34);
    $[56] = autocompleteResults;
    $[57] = defaultRenderItem;
    $[58] = onSelectAutocomplete;
    $[59] = renderItem;
    $[60] = t33;
  } else {
    t33 = $[60];
  }
  let t34;
  if ($[65] !== t30 || $[66] !== t32 || $[67] !== t33) {
    t34 = <CommandList className={t30}>{t32}{t33}</CommandList>;
    $[65] = t30;
    $[66] = t32;
    $[67] = t33;
    $[68] = t34;
  } else {
    t34 = $[68];
  }
  let t35;
  if ($[69] !== t18 || $[70] !== t28 || $[71] !== t34) {
    t35 = <div className="relative w-full"><Command className={t18} shouldFilter={false}>{t28}{t34}</Command></div>;
    $[69] = t18;
    $[70] = t28;
    $[71] = t34;
    $[72] = t35;
  } else {
    t35 = $[72];
  }
  let t36;
  if ($[73] !== geolocationButtonProps || $[74] !== handleLocationError || $[75] !== handleLocationFound || $[76] !== showGeolocationButton) {
    t36 = !showGeolocationButton && <div className="flex justify-center"><GeolocationButton onLocationFound={handleLocationFound} onError={handleLocationError} variant={geolocationButtonProps.variant || "outline"} size={geolocationButtonProps.size || "default"} iconOnly={geolocationButtonProps.iconOnly ?? false} className={geolocationButtonProps.className} /></div>;
    $[73] = geolocationButtonProps;
    $[74] = handleLocationError;
    $[75] = handleLocationFound;
    $[76] = showGeolocationButton;
    $[77] = t36;
  } else {
    t36 = $[77];
  }
  let t37;
  if ($[78] !== errorMessage || $[79] !== showErrorMessage) {
    t37 = showErrorMessage && errorMessage && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-center text-sm text-red-700">{errorMessage}</div>;
    $[78] = errorMessage;
    $[79] = showErrorMessage;
    $[80] = t37;
  } else {
    t37 = $[80];
  }
  let t38;
  if ($[81] !== t14 || $[82] !== t35 || $[83] !== t36 || $[84] !== t37) {
    t38 = <div className={t14}>{t35}{t36}{t37}</div>;
    $[81] = t14;
    $[82] = t35;
    $[83] = t36;
    $[84] = t37;
    $[85] = t38;
  } else {
    t38 = $[85];
  }
  return t38;
}